import { CloseFullscreen, CloudUpload, LocalPrintshop, OpenInFull, Search } from '@mui/icons-material';
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useThemes } from '../../components/Theme/theme';
import { listAccountOpeningBalanceApi, useListAccountOpeningBalanceQuery } from '../../redux/apis/accountOpeningBalance.api';
import OpeningBalanceRecursiveRow from '../../components/OpeningBalance/OpeningBalanceRecursiveRow';
import { useListGroupMasterQuery } from '../../redux/apis/accountGroupMaster.api';
import { useDispatch, useSelector } from 'react-redux';
import arrayToTree from 'array-to-tree';
import DraggableAction from '../../components/Buttons/DraggableAction';
import {
  createAccountOpeningBalancesTransaction,
  deleteAccountGroupMaster,
  deleteAccountMaster,
  getFirstTransactionDate,
  getPartyMasterAccountWise,
} from '../../graphql';
import { showErrorSnackbar, showSuccessSnackbar, showWarningSnackbar } from '../../components/snackBar/snakbar';
import { setNavigationBlocked } from '../../redux/reducers/controllNavigation';
import { API, graphqlOperation } from 'aws-amplify';
import moment from 'moment';
import { MoneyFormatv3 } from '../../library/MoneyFormat';
import CreateAndEditGroupModal from '../../components/modals/CreateAndEditGroupModal';
import DeleteLedgerGroup from '../../components/modals/DeleteLedgerGroup';
import AddNewPartyModal from '../PrimeFigma/Modals/AddNewPartyModal';
import useSaveShortcut from '../../hooks/useSaveShortcut';
import { settingsClose } from '../../redux/reducers/companySkipSave';
import useControlledNavigation from '../../hooks/useControlledNavigation';
import printJS from 'print-js';
import { flattenTree, handleFreeze, searchInAccountList } from '../../utils/common';
import { useDebounce } from '../../hooks/useDebounce';
import { LedgerTransactionModalv2 } from '../LedgerTransaction';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import EnterOpeningStock from '../../components/modals/EnterOpeningStock';
import { LedgerModal, useLedgerModalHandle } from '../../features/AccountsAndGroups';
import { dashboardApi } from '../../redux/apis/dashboardApi';
import { saveOpeningBalance } from '../../utils/openingBalanceUtils';
import { companyPreferenceApi } from '../../redux/apis/companyPreference.api';
import { formatUnixTimestamp } from '../../library';
import {OpeningBalanceDateApi } from '../../redux/apis/openingbalacedate.api';

// Helper functions for date handling
function parsePrefToDayjs(input) {
  if (input == null) return null;

  if (typeof input === 'number') {
    return dayjs.unix(input);
  }

  const d1 = dayjs(input, 'YYYY-MM-DD', true);
  if (d1.isValid()) return d1;

  const d2 = dayjs(input);
  if (d2.isValid()) return d2;

  return null;
}

function clampDate(d, min, max) {
  let out = d;
  if (min && out.isBefore(min, 'day')) out = min;
  if (max && out.isAfter(max, 'day')) out = max;
  return out;
}

function OpeningBalancev2() {
  const { theme } = useThemes();

  const matches = useMediaQuery('(max-width:600px)');

  const dispatch = useDispatch();
  const navigate = useControlledNavigation();

  const [entryDate, setEntryDate] = useState('');
  const [datePickerValue, setDatePickerValue] = useState(null);
  const [sortBy, setSortBy] = useState('all');
  const [expandButtonClicked, setExpandButtonClicked] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState('');
  const [totalCredit, setTotalCredit] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [expandedRows, setExpandedRows] = useState([]);
  const [newLedgerResponse, setNewLedgerResponse] = useState(null);
  const [deleteId, setdeleteId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [backupRow, setBackupRow] = useState([]);
  const [editBalance, setEditBalance] = useState(false);
  const [isDateLoaded, setIsDateLoaded] = useState(false);
  const [isSaveLoading, setSaveLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [lastTranscationDate, setLastTranscationDate] = useState('');
  const [accountID, setAccountID] = useState('');
  const [inputFieldRef, setInputFieldRef] = useState(null);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [draftData, setDraftData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasEnteredTimestamp, setHasEnteredTimestamp] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [localEnteredValues, setLocalEnteredValues] = useState({});
  const initialStockMangState = {
    isModalOpen: false,
    credit: 0,
    debit: 0,
    accountid: null,
  };
  const [stockManagement, setStockManagement] = useState(initialStockMangState);

  const { isBlocked } = useSelector((state) => state.controllNavigationSlice);
  const { financialYear } = useSelector((state) => state.userPrefrenceandReducer);

  // Date picker min/max dates
  const { minDate, maxDate } = useMemo(() => {
    if (!financialYear?.startdate || !financialYear?.enddate) {
      return { minDate: undefined, maxDate: undefined };
    }
    return {
      minDate: dayjs.unix(financialYear.startdate),
      maxDate: dayjs.unix(financialYear.enddate),
    };
  }, [financialYear?.startdate, financialYear?.enddate]);

  const parentRef = useRef(null);

  const initialModalState = {
    groupModal: false,
    ledgerModal: false,
    deleteModal: false,
    partyModal: false,
  };

  const [openModal, setOpenModal] = useState(initialModalState);

  const companyDetails = useSelector((state) => state?.companyReducer.companyDetails);

  const { saveSkipStatus } = useSelector((state) => state.companySipSaveReducer);

  const { startdate, enddate } = useSelector((state) => state.userPrefrenceandReducer.financialYear) || {};

  const {
    data: accountOpeningBalance,
    isLoading: accountloading,
    refetch: refetchAccountOpeningBalance,
  } = useListAccountOpeningBalanceQuery(
    { companyid: companyDetails?.companyid },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: groupMaster, isLoading: groupLoading, refetch: refetchAccountGroup } = useListGroupMasterQuery();

  useEffect(() => {
    fetchLastTranscation();
  }, []);

  useEffect(() => {
    if (deleteId !== '') {
      deleteLedgers();
      setdeleteId('');
    }
  }, [deleteId]);

  // Sync datePickerValue with entryDate
  useEffect(() => {
    if (entryDate) {
      const dayjsDate = parsePrefToDayjs(entryDate);
      if (dayjsDate) {
        setDatePickerValue(clampDate(dayjsDate, minDate, maxDate));
      }
    } else {
      setDatePickerValue(null);
    }
  }, [entryDate, minDate, maxDate]);

  // Memoized date change handler
  const handleDateChange = useCallback((newValue) => {
    if (hasEnteredTimestamp && !isEditMode) {
      return;
    }
    
    if (!newValue) {
      setEntryDate(null);
      return;
    }
    
    const selectedDate = newValue.startOf('day').unix();
    
    // Check if date is within allowed range
    const startDate = financialYear?.startdate || moment().startOf('year').unix();
    const endDate = lastTranscationDate || financialYear?.enddate || moment().endOf('year').unix();
    
    if (selectedDate < startDate || selectedDate > endDate) {
      const startDateFormatted = moment(startDate * 1000).format('DD/MM/YYYY');
      const endDateFormatted = moment(endDate * 1000).format('DD/MM/YYYY');
      const selectedDateFormatted = moment(selectedDate * 1000).format('DD/MM/YYYY');
      
      let errorMessage;
      if (selectedDate < startDate) {
        errorMessage = `Selected date ${selectedDateFormatted} is before the allowed start date ${startDateFormatted}. Please select a date within the range: ${startDateFormatted} - ${endDateFormatted}.`;
      } else if (selectedDate > endDate) {
        errorMessage = `Selected date ${selectedDateFormatted} is after the allowed end date ${endDateFormatted}. Please select a date within the range: ${startDateFormatted} - ${endDateFormatted}.`;
      } else {
        errorMessage = `Selected date ${selectedDateFormatted} is outside the allowed range. Please select a date within: ${startDateFormatted} - ${endDateFormatted}.`;
      }
      
      showErrorSnackbar(errorMessage);
      return;
    }
    
    dispatch(setNavigationBlocked(true));
    setEntryDate(selectedDate);

    // Handle edit mode logic
    if (hasEnteredTimestamp && !isEditMode) {
      setIsEditMode(true);
      setDraftData([{ id: 'dateChange' }]);
      const originalDataCopy = JSON.parse(JSON.stringify(accountOpeningBalance));
      setOriginalData(originalDataCopy);
    } else if (hasEnteredTimestamp && isEditMode) {
      setDraftData((prev) => {
        if (!prev.some((item) => item.id === 'dateChange')) {
          return [...prev, { id: 'dateChange' }];
        }
        return prev;
      });
    } else {
      setDraftData((prev) => {
        if (!prev.some((item) => item.id === selectedDate)) {
          return [...prev, { id: selectedDate }];
        }
        return prev;
      });
    }
  }, [hasEnteredTimestamp, isEditMode, lastTranscationDate, financialYear, dispatch, accountOpeningBalance]);

  // Memoized click handler
  const handleDateFieldClick = useCallback((e) => {
    setInputFieldRef(null);
    if (hasEnteredTimestamp && !isEditMode) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [hasEnteredTimestamp, isEditMode]);

  // Memoized tooltip props
  const tooltipProps = useMemo(() => {
    let title = "";
    
    if (hasEnteredTimestamp && !isEditMode) {
      const startDate = moment((financialYear?.startdate || moment().startOf('year').unix()) * 1000).format('DD/MM/YYYY');
      const endDate = lastTranscationDate 
        ? moment(lastTranscationDate * 1000).format('DD/MM/YYYY')
        : moment((financialYear?.enddate || moment().endOf('year').unix()) * 1000).format('DD/MM/YYYY');
      
      title = `Click 'Enable Edit' to modify the date. Allowed range: ${startDate} - ${endDate}`;
    }
    
    return {
      title,
      placement: "top",
      arrow: true,
      disableHoverListener: !hasEnteredTimestamp || isEditMode,
      disableFocusListener: !hasEnteredTimestamp || isEditMode,
      disableTouchListener: !hasEnteredTimestamp || isEditMode,
    };
  }, [hasEnteredTimestamp, isEditMode, financialYear, lastTranscationDate]);

  // Memoized date picker styles
  const datePickerStyles = useMemo(() => ({
    width: { xs: '120px', sm: '180px' },
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: '#fff',
    fontSize: { xs: '12px', sm: '14px' },
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.25)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255,255,255,0.3)',
    },
    '&.Mui-disabled': {
      color: '#fff !important',
      opacity: 1,
      WebkitTextFillColor: '#fff !important',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.5)',
        borderWidth: '2px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255,255,255,0.8)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#fff',
        borderWidth: '2px',
      },
      '&.Mui-disabled fieldset': {
        borderColor: 'rgba(255,255,255,0.5)',
        borderWidth: '2px',
      },
    },
    '& .MuiInputBase-input': {
      color: '#fff',
      '&::placeholder': {
        color: 'rgba(255,255,255,0.8)',
        opacity: 1,
      },
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#fff !important',
      opacity: 1,
      fontWeight: 500,
      WebkitTextFillColor: '#fff !important',
    },
    '& .MuiSvgIcon-root': {
      color: '#fff !important',
      filter: 'invert(1)',
      fill: '#fff !important',
    },
    '& .MuiIconButton-root': {
      color: '#fff !important',
      '& .MuiSvgIcon-root': {
        color: '#fff !important',
        fill: '#fff !important',
      },
    },
  }), []);

  // Constants for better maintainability
  const DIFFERENCE_ACCOUNT_ID = 12;

  useEffect(() => {
    if (accountOpeningBalance?.length > 0 && !isDateLoaded) {
      const openingBalanceDiffAC = accountOpeningBalance.find((obj) => obj.accountid === DIFFERENCE_ACCOUNT_ID);

      if (openingBalanceDiffAC) {
        const { enteredtimestamp } = openingBalanceDiffAC;
        const hasTimestamp = Boolean(enteredtimestamp);

        // Batch state updates for better performance
        setEntryDate(enteredtimestamp || startdate || moment().startOf('day').unix());
        setIsDateLoaded(true);
        setHasEnteredTimestamp(hasTimestamp);
        setIsEditMode(!hasTimestamp);
      }
    }
  }, [accountOpeningBalance, startdate]);

  useEffect(() => {
    if (shouldNavigate && !isBlocked) {
      navigate('/companysetup');
      setShouldNavigate(false);
    }
  }, [isBlocked, shouldNavigate]);

  useEffect(() => {
    if (accountOpeningBalance?.length > 0 && groupMaster?.length > 0) {
      createTree(accountOpeningBalance);
    }
  }, [groupMaster, accountOpeningBalance]);

  // Restore local values after data refetch
  useEffect(() => {
    if (Object.keys(localEnteredValues).length > 0 && accountOpeningBalance?.length > 0) {
      dispatch(
        listAccountOpeningBalanceApi.util.updateQueryData(
          'listAccountOpeningBalance',
          { companyid: companyDetails?.companyid },
          (draft) => {
            Object.entries(localEnteredValues).forEach(([accountId, values]) => {
              const account = draft.find((acc) => acc?.accountid === parseInt(accountId));
              if (account && values) {
                account.debitamount = values.debit || 0;
                account.creditamount = values.credit || 0;
              }
            });
          }
        )
      );
    }
  }, [accountOpeningBalance, localEnteredValues, companyDetails?.companyid, dispatch]);

  useEffect(() => {
    if (expandButtonClicked === true) {
      if (backupRow.length > 0) {
        handleExpandAll();
      }
    } else {
      handleCollapseAll();
    }
  }, [expandButtonClicked, backupRow]);


  useEffect(() => {
    const fetchSearchResults = async () => {
      if ((searchText && sortBy) || sortBy !== 'all') {
        const searchResult = searchInAccountList(backupRow, searchText, sortBy);
        const flattenData = flattenTree(searchResult);
        setRows(flattenData);
      } else {
        setRows(backupRow);
      }
    };
    if (!editBalance) fetchSearchResults();
  }, [searchText, sortBy, backupRow, editBalance]);

  //   functions
  async function deleteLedgers() {
    try {
      setIsModalLoading(true);

      if (deleteId.category === 'group') {
        const resDeleteGroup = await API.graphql(
          graphqlOperation(deleteAccountGroupMaster, {
            accountgroupid: deleteId.id,
          })
        );

        const { statuscode, statusmessage } = resDeleteGroup?.data?.deleteAccountGroupMaster || {};

        if (statuscode === 'Error') {
          showWarningSnackbar(statusmessage);
          return;
        }

        showSuccessSnackbar('Group deleted successfully');
        await refetchAccountGroup();
      } else if (deleteId.category === 'ledger') {
        const resDeleteLedger = await API.graphql(
          graphqlOperation(deleteAccountMaster, {
            accountsid: deleteId.id,
          })
        );

        const { statuscode, statusmessage } = resDeleteLedger?.data?.deleteAccountMaster || {};

        if (statuscode === 'Error') {
          if (statusmessage.includes('Transactions Already Recorded')) {
            showWarningSnackbar('Cannot delete ledger account : Transactions exists in the current year');
          } else {
            showWarningSnackbar(statusmessage);
          }
        } else {
          showSuccessSnackbar('Ledger deleted successfully');
          await refetchAccountOpeningBalance();
        }
      }
    } catch (error) {
      showErrorSnackbar('Failed to delete. Please try again.');
    } finally {
      setIsModalLoading(false);
    }
  }
  const handleOpenModal = (key) => {
    if (key in openModal) {
      setOpenModal((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  };

  function handleSortBY(e) {
    setSortBy(e.target.value);
    setRows(backupRow);
  }

  async function fetchLastTranscation(params) {
    try {
      const response = await API.graphql(graphqlOperation(getFirstTransactionDate));

      const { transactiontimestamp } = response?.data?.getFirstTransactionDate;

      setLastTranscationDate(transactiontimestamp);
    } catch (error) {
      showErrorSnackbar('Something went wrong, Please refesh page');
    }
  }

  async function createTree(tbData) {
    try {
      if (!tbData || !groupMaster || !companyDetails?.relatedcompanydetailsnew?.[0]) return;

      const treeArray = [];
      const relatedDetails = companyDetails.relatedcompanydetailsnew[0];

      const isRegType1 = relatedDetails.registrationtype === 1;

      const createLedgerNode = (account, parentId) => ({
        name: account.accountname,
        category: 'ledger',
        debit: account.debitamount,
        credit: account.creditamount,
        groupName: account.accountname,
        id: account.accountid,
        _id: `${account.accountid}ledger`,
        key: `${account.accountid}ledger`,
        defaultGroupId: account.accountgroupid,
        parent: parentId,
        type: account.accounttypecd,
        restrictedflag: account.restrictedflag,
        path: account.path,
      });

      const createGroupNode = (group) => ({
        name: group.accountgroupname,
        category: 'group',
        debit: 0,
        credit: 0,
        groupName: '',
        id: group.accountgroupid,
        _id: group.accountgroupid.toString(),
        key: group.accountgroupid.toString(),
        restrictedflag: group.restrictedflag,
        defaultGroupId: group.restrictedflag === 1 || group.accountgroupid === 37 ? group.accountgroupid : null,
        parent: group.accountgroupid === group.accountparentgroupid ? null : group.accountparentgroupid,
        path: group.path,
        type: group.accounttypecd,
      });

      let forceDisplayGRoup = [];

      tbData.forEach((account) => {
        const hasBalance = account.debitamount !== 0 || account.creditamount !== 0;
        if (isRegType1 && account?.path?.split('/')?.some((seg) => ['31', '32', '33'].includes(seg))) {
          if (hasBalance) {
            forceDisplayGRoup.push(account.accountgroupid);
          } else {
            return;
          }
        }

        if (isRegType1 && !hasBalance && [6, 7, 8, 9, 56, 57, 58, 59, 60, 61, 62, 63].includes(account.accountid)) {
          return;
        }

        if (account.accountid === 12 && account.accounttypecd === 'BS') {
          return;
        }
        if (account.accountid === 23 && account.accounttypecd === 'BS') {
          return;
        }

        if (account?.is_active === 0 && !hasBalance) {
          return;
        }

        const parentId = account.accountgroupid;
        treeArray.push(createLedgerNode(account, parentId));
      });

      groupMaster.forEach((group) => {
        if (group.accounttypecd !== 'BS') return;

        if (isRegType1 && !forceDisplayGRoup.includes(group.accountgroupid) && [(31, 32, 33)].includes(group.accountgroupid)) return;

        treeArray.push(createGroupNode(group));
      });

      const treeRow = arrayToTree(treeArray, {
        parentProperty: 'parent',
        customID: '_id',
      });

      const updatedDataRows = updateGroupBalances(treeRow);
      const flatedData = flattenTree(updatedDataRows);

      const finalRows = searchText && sortBy ? searchInAccountList(flatedData, searchText, sortBy) : flatedData;

      setRows(finalRows);
      setBackupRow(flatedData);
      setExpandedRows(flatedData.map((row) => row.id));
    } catch (error) {
      showErrorSnackbar('An error occurred. Please refresh the page and try again.');
    }
  }

  function updateGroupBalances(data) {
    let totalDebits = 0;
    let totalCredits = 0;

    const parseAmount = (value) => {
      if (typeof value === 'string') {
        value = value.replace(/,/g, '');
        if (value.endsWith('.')) {
          value = value.slice(0, -1);
        }
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const processGroup = (group) => {
      let groupDebit = 0;
      let groupCredit = 0;

      if (group?.children?.length) {
        group.children.forEach((child) => {
          if (child?.category === 'ledger') {
            groupDebit += parseAmount(child?.debit);
            groupCredit += parseAmount(child?.credit);
          } else if (child?.category === 'group') {
            const { totalDebit, totalCredit } = processGroup(child);
            groupDebit += totalDebit;
            groupCredit += totalCredit;
          }
        });
      }

      if (group.category === 'group') {
        group.debit = groupDebit;
        group.credit = groupCredit;
      }

      return { totalDebit: groupDebit, totalCredit: groupCredit };
    };

    data.forEach((group) => {
      const { totalDebit, totalCredit } = processGroup(group);
      totalDebits += totalDebit;
      totalCredits += totalCredit;
    });

    if (typeof setTotalDebit === 'function') setTotalDebit(totalDebits);
    if (typeof setTotalCredit === 'function') setTotalCredit(totalCredits);

    return data;
  }

  const updateLedger = (id, type, value, fromStockModal = false) => {
    const newValue = parseFloat(value) || 0;

    // If called from stock modal and not in edit mode, enable edit mode
    if (fromStockModal && hasEnteredTimestamp && !isEditMode) {
      setIsEditMode(true);
      const originalDataCopy = JSON.parse(JSON.stringify(accountOpeningBalance));
      setOriginalData(originalDataCopy);
    }

    // Store in local state for persistence
    setLocalEnteredValues(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: newValue,
        // Clear the other type when one is entered
        [type === 'debit' ? 'credit' : 'debit']: 0
      }
    }));

    // Only add to draft data if there's an actual change from original
    if (originalData && (isEditMode || fromStockModal)) {
      const originalAccount = originalData.find((acc) => acc.accountid === id);

      if (originalAccount) {
        const originalValue = type === 'debit' ? originalAccount.debitamount : originalAccount.creditamount;
        if (Math.abs(originalValue - newValue) > 0.01) {
          // Check if value actually changed
          setDraftData((prev) => {
            if (!prev.some((item) => item?.id === id)) {
              return [...prev, { id }];
            }
            return prev;
          });
        }
      } else {
        // New ledger not in original data - add to draft if value > 0
        if (newValue > 0) {
          setDraftData((prev) => {
            if (!prev.some((item) => item?.id === id)) {
              return [...prev, { id }];
            }
            return prev;
          });
        }
      }
    } else {
      // For first-time save, always add to draft data if value > 0
      if (newValue > 0) {
        setDraftData((prev) => {
          if (!prev.some((item) => item?.id === id)) {
            return [...prev, { id }];
          }
          return prev;
        });
      }
    }

    debouncedChange(id, value, type);
    dispatch(setNavigationBlocked(true));
  };

  const debouncedChange = useCallback(
    useDebounce(
      (id, value, type) => {
        dispatch(
          listAccountOpeningBalanceApi.util.updateQueryData(
            'listAccountOpeningBalance',
            { companyid: companyDetails?.companyid },
            (draft) => {
              const account = draft.find((acc) => acc?.accountid === id);
              if (account) {
                // Use local state values if available, otherwise use the passed value
                const localValues = localEnteredValues[id];
                if (localValues) {
                  account.debitamount = localValues.debit || 0;
                  account.creditamount = localValues.credit || 0;
                } else {
                  if (type === 'debit') {
                    account.creditamount = 0;
                    account.debitamount = parseFloat(parseFloat(value).toFixed(2));
                  } else {
                    account.creditamount = parseFloat(parseFloat(value).toFixed(2));
                    account.debitamount = 0;
                  }
                }
              }
            }
          )
        );
      },
      400,
      false
    ),
    [localEnteredValues, companyDetails?.companyid]
  );

  const toggleExpandRow = (id) => {
    let result = [...rows];

    if (expandedRows.includes(id)) {
      result = rows.filter((item) => !item?.path?.split('/').includes(String(id)) || item?.id === id);

      const idsToRemove = new Set(backupRow.filter((item) => item?.path?.split('/').includes(String(id))).map((item) => item?.id));
      idsToRemove.add(id);

      setExpandedRows(expandedRows.filter((item) => !idsToRemove.has(item)));
    } else {
      let filteredData = backupRow.filter((item) => item?.parent === id);
      const index = result.findIndex((item) => item?.id === id);
      if (index !== -1) {
        result.splice(index + 1, 0, ...filteredData);
      } else {
        result = [...result, ...filteredData];
      }

      setExpandedRows([...expandedRows, id]);
    }

    setRows(result);
  };

  const handleExpandAll = () => {
    setExpandedRows(backupRow.map((row) => row?.id));
    setRows(backupRow);
  };

  const handleCollapseAll = () => {
    setExpandedRows([]);
    const collapseData = backupRow.filter((item) => item?.category === 'group' && (item?.id === 1 || item?.id === 10));
    setRows(collapseData);
  };

  function handleCancel() {
    navigate('/');
  }

  async function saveCreditDebitDetails(action) {
    try {
      if (!hasEnteredTimestamp) {
        const hasAnyValues = accountOpeningBalance.some(
          (account) => account.accountid !== 12 && (account.debitamount > 0 || account.creditamount > 0)
        );
        if (!hasAnyValues) {
          showWarningSnackbar('Please enter opening balance values for at least one ledger account to save');
          return;
        }
      }
      else if (draftData?.length === 0 || (draftData?.length === 1 && draftData[0]?.id === 'dateChange' && !hasEnteredTimestamp)) {
        showWarningSnackbar('No changes made.');
        setIsEditMode(false);
        return;
      }

      if (action && action === 'save') {
        setSaveLoading(true);
      }

      const opbTransaction = {
        bankreferencenumber: '',
        comments: '',
        entrydate: '',
        transactiontypecd: 'OpeningBalance',
        vouchercode: 0,
      };

      const total = accountOpeningBalance
        .filter((account) => account?.accountid !== 12)
        .reduce(
          (acc, item) => {
            acc.creditAmount += item.creditamount || 0;
            acc.debitAmount += item.debitamount || 0;
            return acc;
          },
          { creditAmount: 0, debitAmount: 0 }
        );

      const debitCreditEntry = {
        accountid: 12,
        comments: '',
        startdate: '',
        creditamount: total?.debitAmount > total?.creditAmount ? total?.debitAmount - total?.creditAmount : 0,
        debitamount: total?.creditAmount > total?.debitAmount ? total?.creditAmount - total?.debitAmount : 0,
        transactiontypecd: 'OpeningBalance',
        companybranchid: 0,
      };

      const allAccounts = accountOpeningBalance
        .filter((account) => account.accountid !== 12)
        .map((account) => ({
          accountid: account?.accountid,
          comments: '',
          creditamount: parseFloat(account?.creditamount) || 0,
          debitamount: parseFloat(account?.debitamount) || 0,
          financialyearid: 0,
          startdate: '',
          transactiontypecd: 'OpeningBalance',
          companybranchid: 0,
        }));

      const masterData = [debitCreditEntry, ...allAccounts];

      const transactionMasterJson = JSON.stringify([opbTransaction]);
      const transactionDetailsJson = JSON.stringify(masterData);

      const response = await API.graphql(
        graphqlOperation(createAccountOpeningBalancesTransaction, {
          createAccountOpeningBalancesArrayInput: {
            openingbalancedate: entryDate,
            transactionmaster: transactionMasterJson,
            transactiondetails: transactionDetailsJson,
          },
        })
      );

      const result = response?.data?.createAccountOpeningBalancesTransaction;

      if (result?.statuscode === 'Success') {
        if (action && action !== 'edit') {
          setDraftData([]);
          setLocalEnteredValues({}); // Clear local values after successful save
           dispatch(companyPreferenceApi.util.invalidateTags?.(['listCompanyPreference']));
    dispatch(
  OpeningBalanceDateApi.util.invalidateTags([
    { type: 'OpeningBalanceDateApi', id: 'ALL' },
  ])
);

          showSuccessSnackbar(result.statusmessage);
        }

        dispatch(listAccountOpeningBalanceApi.util.invalidateTags(['listAccountOpeningBalance']));
        dispatch(dashboardApi.util.invalidateTags(['dashboard',"bizview"]));

        // After successful save, update state
        setHasEnteredTimestamp(true);
        setIsEditMode(false); // Reset to view mode after save
        setOriginalData(null); // Clear original data after save

        if (action === 'save') {
          dispatch(setNavigationBlocked(false));
        }
      }
    } catch (error) {
      if (error?.errors[0]?.message?.includes('date constraints')) {
        const errorMessage = `Transactions already exist before ${moment(entryDate * 1000).format(
          'DD/MM/YYYY'
        )}. Please change the Opening Balance date.`;
        showErrorSnackbar(errorMessage);
        setOpenModal(initialModalState);
      }
      if (error?.errors[0]?.message.includes('Transactions already exist')) {
        showErrorSnackbar('Transactions already exist before the Opening Balance Date');
      } else {
        showErrorSnackbar('An unexpected error occurred while saving details. Please try again later.');
      }
    } finally {
      setSaveLoading(false);
    }
  }

  function recursiveRowFn(rows) {
    return rows
      .map((row) => {
        if (row.children && row.children.length > 0) {
          return [row, ...recursiveRowFn(row.children)];
        }
        return row;
      })
      .flat();
  }

  function handlePrint() {
    const dataRow = recursiveRowFn(rows);

    const printableTable = `
    <style>
      @media print {
        tr.group-row {
          background-color: #d3d3d3 !important;
          -webkit-print-color-adjust: exact; 
          print-color-adjust: exact; 
        }
        th {
          background-color: #f4f4f4 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    </style>
    <table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 14px;">
      <thead>
        <tr>
          <th style="border: 1px solid #000; padding: 8px;">Account</th>
          <th style="border: 1px solid #000; padding: 8px; text-align: right;">Debit</th>
          <th style="border: 1px solid #000; padding: 8px; text-align: right;">Credit</th>
        </tr>
      </thead>
      <tbody>
        ${dataRow
        .map((row) => {
          const isGroupRow = row.category === 'group';
          return `
              <tr class="${isGroupRow ? 'group-row' : ''}">
                <td style="border: 1px solid #000; padding: 8px;">${row?.name}</td>
                <td style="border: 1px solid #000; padding: 8px; text-align: right;">₹ ${row?.debit?.toFixed(2) || '0.00'}</td>
                <td style="border: 1px solid #000; padding: 8px; text-align: right;">₹ ${row?.credit?.toFixed(2) || '0.00'}</td>
              </tr>
            `;
        })
        .join('')}
      </tbody>
    </table>
  `;

    printJS({
      printable: printableTable,
      type: 'raw-html',
      header: "<h3 style='text-align: center;'>Account Summary</h3>",
    });
  }

  useSaveShortcut(() => {
    if (saveSkipStatus == false) {
      if (window.location.href.includes('companysetup')) {
        showWarningSnackbar('Please Complete the Company Setup Details');
      }
      dispatch(settingsClose());
      navigate('/companysetup');
    } else {
      saveCreditDebitDetails('save');
    }
  });

  const memoizedRows = useMemo(() => rows, [rows]);

  const rowVirtualizer = useWindowVirtualizer({
    count: memoizedRows.length,
    estimateSize: () => 45,
    overscan: 25,
  });

  const totalSize = rowVirtualizer.getTotalSize();

  const Freeze = useSelector((state) => state.userPrefrenceandReducer?.financialYear?.freezestatus);

  const { openLedgerModal, closeLedgerModal, ledgerModalParams } = useLedgerModalHandle();

  const handleSelectRow = useCallback((row) => {
    setSelectedRow(row);

    if (Freeze === 1) return handleFreeze();

    const { modal, category, action, id, parent, name } = row || {};

    if (modal === 'ledger') {
      openLedgerModal({
        open: true,
        groupId: category === 'group' ? id : parent,
        accountId: action === 'edit' ? id : '',
        title: action === 'edit' ? name : '',
        filterAccountTypeCd: 'BS',
        restrictedflag: row?.restrictedflag,
      });
    } else {
      handleOpenModal(action === 'delete' ? 'deleteModal' : 'groupModal');
    }
  }, []);

  const handleLedgerModal = ({ accountsId }) => {
    setNewLedgerResponse(selectedRow?.action === 'create' ? accountsId + 'ledger' : null);
    setSelectedRow(null);
  };

  const typographyRef = useRef(null);

  return (
    <Box
      sx={{
        paddingBottom: '15px',
      }}
    >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={accountloading || groupLoading || isSaveLoading || isModalLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          borderTopRightRadius: '12px',
          borderTopLeftRadius: '12px',
          padding: '1px 8px',
          color: '#ffff',
          backgroundColor: 'primary.main',
          boxSizing: 'border-box',
        }}
      >
        <Stack
          direction="row"
          spacing={3}
          px="8px "
          alignItems="center"
          sx={{ flex: 1, color: '#ffff', marginTop: { xs: '15px', sm: '0px' } }}
        >
          <Typography variant="h1" fontWeight="bold" color="#ffff">
            Opening Balance
          </Typography>

          <Box>
            <ButtonGroup sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                select
                id="Group"
                label=""
                variant="outlined"
                size="small"
                value={sortBy}
                onChange={handleSortBY}
                sx={{
                  width: 100,
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                    transform: 'translateY(-1px)',
                  },
                  '.MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(0,0,0,0.1)',
                  },
                  '.MuiInputBase-input': {
                    color: 'primary.main',
                    fontWeight: 500,
                    padding: '8px 14px',
                  },
                  '.MuiSelect-icon': {
                    color: 'primary.main',
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="group">Group</MenuItem>
                <MenuItem value="ledger">Ledger</MenuItem>
              </TextField>
              <TextField
                id="amount"
                placeholder="Search..."
                variant="outlined"
                size="small"
                type="text"
                value={searchText}
                onClick={(e) => setInputFieldRef(null)}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{
                  minWidth: 250,
                  '.MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.25)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    },
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.4)',
                    transition: 'all 0.3s ease',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.6)',
                  },
                  '.MuiInputBase-input': {
                    color: 'white',
                    '&::placeholder': {
                      color: 'rgba(255,255,255,0.8)',
                      opacity: 1,
                    },
                  },
                  '.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                    borderWidth: '1px',
                  },
                }}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'rgba(255,255,255,0.8)', mr: 1 }} />,
                }}
              />{' '}
            </ButtonGroup>{' '}
          </Box>
        </Stack>
        <Box
          sx={{
            marginTop: '10px',
            paddingBottom: '10px',
            display: 'flex',
            justifyContent: { xs: 'space-around', md: 'end' },
            color: 'white',
          }}
        >
          <Tooltip title="Print">
            <Button
              disableRipple
              disableElevation
              variant="outlined"
              sx={{
                fontSize: '12px',
                marginBottom: { xs: '20px', md: '0px' },
                marginLeft: { xs: '0px', sm: '20px' },
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: 'white',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
              onClick={() => handlePrint()}
            >
              <LocalPrintshop />
            </Button>
          </Tooltip>

          <Tooltip
            title="Rolling out soon"
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#ffd700',
                  color: 'black',
                  border: 'solid 1px #ffd700',
                  width: '100px',
                  position: 'absolute',
                  left: '-60px',
                },
              },
            }}
          >
            <Button
              disableRipple
              disableElevation
              variant="outlined"
              sx={{
                fontSize: '12px',
                marginBottom: { xs: '20px', md: '0px' },
                marginLeft: { xs: '0px', sm: '20px' },
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: 'white',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <CloudUpload sx={{ color: '#ffa500', opacity: 0.3 }} />
            </Button>
          </Tooltip>

          <Tooltip title="Expand/Collapse">
            <Button
              disableRipple
              disableElevation
              variant="outlined"
              sx={{
                fontSize: '12px',
                marginBottom: { xs: '20px', md: '0px' },
                marginLeft: { xs: '0px', sm: '20px' },
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: 'white',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
              onClick={() => setExpandButtonClicked((prev) => !prev)}
            >
              {!expandButtonClicked ? <OpenInFull /> : <CloseFullscreen />}
            </Button>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ width: '100%',boxSizing: 'border-box' }}>
        <Box
          sx={{
            mt: '2px',
            minWidth: { xs: 'fit-content' },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: '#ffff',
              display: 'flex',
            }}
          >
            <Box
              sx={{
                width: { xs: '250px', sm: '60%' },
                height: '48px',
                borderRight: '4px solid',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? '#26262F' : '#EEEEFA',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  height: '100%',
                  pl: 2,
                  alignItems: 'center',
                }}
              >
                <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                  Accounts
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    pl: { xs: 1, sm: 2 },
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '10px', sm: '15px' },
                      color: '#fff',
                      fontWeight: 500,
                    }}
                  >
                    As on {<span style={{ display: matches ? 'none' : 'inline' }}>:</span>}{' '}
                  </Typography>
                  <Tooltip {...tooltipProps}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={datePickerValue}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        minDate={minDate}
                        maxDate={maxDate}
                        disabled={hasEnteredTimestamp && !isEditMode}
                        slotProps={{
                          textField: {
                            size: 'small',
                            onClick: handleDateFieldClick,
                            sx: datePickerStyles,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Tooltip>

                  {/* Date range helper text
                  <Typography
                    sx={{
                      fontSize: { xs: '10px', sm: '12px' },
                      color: 'rgba(255,255,255,0.8)',
                      fontWeight: 600,
                      mt: 0.2,
                      ml: 1,
                    }}
                  >
                    Allowed: {formatUnixTimestamp(financialYear?.startdate)} to {lastTranscationDate ? formatUnixTimestamp(lastTranscationDate) : formatUnixTimestamp(financialYear?.enddate)}
                  </Typography> */}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: { xs: '125px', sm: '20%' },
                width: { xs: '125px', sm: '20%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '4px solid',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? '#26262F' : '#EEEEFA',
              }}
            >
              <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                Debit
              </Typography>
            </Box>
            <Box
              sx={{
                minWidth: { xs: '125px', sm: '20%' },
                width: { xs: '125px', sm: '20%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                Credit
              </Typography>
            </Box>
          </Box>

          <Box
            ref={parentRef}
            sx={{
              borderRight: '1px solid #ccc',
              borderLeft: '1px solid #ccc',
              msOverflowStyle: 'none',
            }}
          >
            <Box sx={{ height: totalSize, width: '100%', position: 'relative' }}>
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = memoizedRows[virtualRow.index];
                return (
                  <Box
                    key={virtualRow.key}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: virtualRow.size,
                      transform: `translateY(${virtualRow.start}px)`,
                      borderBottom: '1px solid #ccc',
                    }}
                  >
                    <OpeningBalanceRecursiveRow
                      row={item}
                      updateLedger={updateLedger}
                      selectedRow={selectedRow}
                      setSelectedRow={setSelectedRow}
                      setGroupId={setGroupId}
                      newLedgerResponse={newLedgerResponse}
                      setNewLedgerResponse={setNewLedgerResponse}
                      toggleExpandRow={toggleExpandRow}
                      expandedRows={expandedRows}
                      setAccountID={setAccountID}
                      setInputFieldRef={setInputFieldRef}
                      inputFieldRef={inputFieldRef}
                      totalSize={totalSize}
                      handleSelectRow={handleSelectRow}
                      setStockManagement={setStockManagement}
                      inputDisabled={hasEnteredTimestamp && !isEditMode}
                      theme={theme}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ color: 'red', display: 'flex', border: '1px solid #e0e0e0' }}>
            <Box
              sx={{
                width: { xs: '250px', sm: '60%' },
                height: '48px',
                borderRight: '4px solid ',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? '#26262F' : '#EEEEFA',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  height: '100%',
                  pl: 2,
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '12px', sm: '14px', lg: '16px' },
                  }}
                >
                  Difference In Opening Balance
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: { xs: '125px', sm: '20%' },
                width: { xs: '125px', sm: '20%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                borderRight: '4px solid',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? '#26262F' : '#EEEEFA',
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 'bold',
                  px: 1,
                  fontSize: { xs: '12px', sm: '14px', lg: '16px' },
                }}
              >
                ₹ {totalCredit - totalDebit > 0 ? MoneyFormatv3(totalCredit - totalDebit) : MoneyFormatv3(0)}
              </Typography>
            </Box>
            <Box
              sx={{
                minWidth: { xs: '125px', sm: '20%' },
                width: { xs: '125px', sm: '20%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 'bold',
                  px: 1,
                  fontSize: { xs: '12px', sm: '14px', lg: '16px' },
                }}
              >
                ₹ {totalDebit - totalCredit > 0 ? MoneyFormatv3(totalDebit - totalCredit) : MoneyFormatv3(0)}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              border: '1px solid #e0e0e0',
              backgroundColor: theme?.palette?.TmId.id.toString().includes('9') ? '#373a36' : theme?.palette?.bodyGray.tertiary,
            }}
          >
            <Box
              sx={{
                width: { xs: '250px', sm: '60%' },
                height: '48px',
                borderRight: '4px solid ',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  height: '100%',
                  pl: 2,
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 'bold',
                    color: theme?.palette?.TmId.id.toString().includes('9') ? '#ffff' : '#000000',
                  }}
                >
                  Total
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: { xs: '125px', sm: '20%' },
                width: { xs: '125px', sm: '20%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                borderRight: '4px solid',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '12px', sm: '14px', lg: '16px' },
                  px: 1,
                  color: theme?.palette?.TmId.id.toString().includes('9') ? '#ffff' : '#000000',
                }}
                ref={typographyRef}
              >
                ₹ {MoneyFormatv3(totalCredit - totalDebit > 0 ? totalDebit + (totalCredit - totalDebit) : totalDebit + 0)}
              </Typography>
            </Box>
            <Box
              sx={{
                minWidth: { xs: '125px', sm: '20%' },
                width: { xs: '125px', sm: '20%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '12px', sm: '14px', lg: '16px' },
                  px: 1,
                  color: theme?.palette?.TmId.id.toString().includes('9') ? '#ffff' : '#000000',
                }}
              >
                ₹ {MoneyFormatv3(totalDebit - totalCredit > 0 ? totalCredit + (totalDebit - totalCredit) : totalCredit + 0)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <DraggableAction
        actionType={isEditMode && hasEnteredTimestamp ? 'edit' : 'save'}
        textToCancel="Back to Dashboard"
        onExit={() => {
          handleCancel();
        }}
        onSave={
          isEditMode
            ? () => {
              if (saveSkipStatus == false) {
                dispatch(setNavigationBlocked(false));
                setShouldNavigate(true);
                if (window.location.href.includes('companysetup')) {
                  showWarningSnackbar('Please Complete the Company Setup Details');
                }
                dispatch(settingsClose());
              } else {
                saveCreditDebitDetails('save');
              }
            }
            : null
        }
        onEnableEdit={() => {
          if (hasEnteredTimestamp && !isEditMode) {
            setIsEditMode(true);
            setDraftData([]);
            const originalDataCopy = JSON.parse(JSON.stringify(accountOpeningBalance));
            setOriginalData(originalDataCopy);
            dispatch(setNavigationBlocked(false));
          } else {
            showWarningSnackbar('You are already in edit mode');
          }
        }}
      />

      {/*====== Modals ======= */}

      {openModal.groupModal && (
        <CreateAndEditGroupModal
          open={openModal.groupModal}
          setOpen={() => handleOpenModal('groupModal')}
          groupId={selectedRow?.id}
          action={selectedRow?.action}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setNewLedgerResponse={setNewLedgerResponse}
          page={'openingBalance'}
          tab={'all'}
        />
      )}

      <LedgerModal {...ledgerModalParams} onSave={handleLedgerModal} onClose={closeLedgerModal} />

      {openModal.deleteModal && (
        <DeleteLedgerGroup
          open={openModal.deleteModal}
          setOpen={() => handleOpenModal('deleteModal')}
          setdeleteId={setdeleteId}
          selectedRow={selectedRow}
          setsuccessSnakbar={showSuccessSnackbar}
          setSnakbarTex={() => { }}
        />
      )}

      {openModal.partyModal && (
        <AddNewPartyModal
          open={openModal.partyModal}
          setOpen={() => handleOpenModal('partyModal')}
          partyType="Add Party"
          Type={'Party'}
          setNewPartyID={(newId) => {
            dispatch(listAccountOpeningBalanceApi.util.invalidateTags(['listAccountOpeningBalance']));
          }}
          setPartyType={() => { }}
          partyGroupId={selectedRow.category === 'group' ? selectedRow.id : selectedRow.parent}
          page="Balance Sheet"
          partyParamsID={null}
          newPartyName={selectedRow?.name}
        />
      )}

      {accountID && <LedgerTransactionModalv2 open={accountID} accountId={accountID} onClose={() => setAccountID('')} />}
      {stockManagement?.isModalOpen && (
        <EnterOpeningStock
          data={stockManagement}
          updateLedger={updateLedger}
          onClose={() => setStockManagement(initialStockMangState)}
          entryDate={entryDate}
          onSaveOpeningBalance={saveOpeningBalance}
          accountOpeningBalance={isEditMode && originalData ? originalData : accountOpeningBalance}
        />
      )}
    </Box>
  );
}

export default memo(OpeningBalancev2);
